import axios from 'axios';
import logger from './logger';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_BASE = `${BACKEND_URL}/api`;
const CONTROL_ROOM_URL = (process.env.REACT_APP_CONTROL_ROOM_URL || '').replace(/\/+$/, '');
const TOKEN_KEY = 'cr_access_token';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests for Inventory backend
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  logger.info('[API Interceptor] Request to:', config.baseURL + config.url);
  logger.info('[API Interceptor] Token present:', !!token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    logger.info('[API Interceptor] Authorization header set');
  } else {
    logger.info('[API Interceptor] No token, skipping Authorization header');
  }
  return config;
});

// Auth - Call Control Room directly with standardized contract
// Request: { user_email, password, app_code }
// Response: { access_token, user, allowed_apps, roles }
export const login = async (email, password) => {
  const response = await axios.post(`${CONTROL_ROOM_URL}/api/auth/login`, {
    user_email: email,
    password: password,
    app_code: 'INVENTORY'
  });
  return response.data;
};

// Get current user info - Call Inventory backend /api/inventory/context
// This is the correct endpoint for this app (not /api/me)
export const getMe = async () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const url = `${BACKEND_URL}/api/inventory/context`;
  
  logger.info('[API] getMe URL:', url);
  logger.info('[API] Token present:', !!token);
  
  const response = await axios.get(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

// Get outlets for the user
export const getOutlets = async () => {
  logger.info('[API] Fetching outlets');
  const response = await api.get('/outlets');
  return response.data;
};

// Get theme (no outlet required)
export const getTheme = async () => {
  logger.info('[API] Fetching theme');
  const response = await api.get('/theme');
  return response.data;
};

// Global Sync — pulls latest from all external services
export const globalSync = async (force = false) => {
  const response = await api.post(`/sync/all?force=${force}`);
  return response.data;
};

// Sync Status — full sync state per source (v2 truth surface)
export const getSyncStatus = async () => {
  const response = await api.get('/sync/status');
  return response.data;
};

// Force retry a single source (bypasses cooldown, respects locks)
export const forceRetrySource = async (source) => {
  const response = await api.post(`/sync/force-retry?source=${source}`);
  return response.data;
};

// Get unresolved dependency queue items
export const getUnresolved = async () => {
  const response = await api.get('/sync/unresolved');
  return response.data;
};

// Get unresolved items grouped by reason category
export const getUnresolvedGrouped = async () => {
  const response = await api.get('/sync/unresolved/grouped');
  return response.data;
};

// Dismiss a queued item (does NOT fix root cause)
export const resolveUnresolved = async (queueId) => {
  const response = await api.post(`/sync/unresolved/${queueId}/resolve`);
  return response.data;
};

// Mark item as intentionally non-consumable (discounts, packaging, etc)
export const ignoreUnresolved = async (queueId) => {
  const response = await api.post(`/sync/unresolved/${queueId}/ignore`);
  return response.data;
};

// Map unresolved item to ingredient (sends to Expense Capture upstream)
export const mapUnresolvedToIngredient = async (queueId, ingredientId, ingredientName) => {
  const response = await api.post(`/sync/unresolved/${queueId}/map-ingredient`, {
    ingredient_id: ingredientId,
    ingredient_name: ingredientName,
  });
  return response.data;
};

// Search ingredients for mapping
export const searchIngredients = async (query) => {
  const response = await api.get(`/ingredients/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

// Flag unit mismatch issue for upstream fix (Expense Capture / Control Room)
export const flagUnitIssue = async (queueId) => {
  const response = await api.post(`/sync/unresolved/${queueId}/flag-unit-issue`);
  return response.data;
};

// Dashboard
export const getDashboardSnapshot = async (outletId) => {
  const response = await api.get(`/dashboard/snapshot?outlet_id=${outletId}`);
  return response.data;
};

export const getDashboardAlerts = async (outletId) => {
  const response = await api.get(`/dashboard/alerts?outlet_id=${outletId}`);
  return response.data;
};

export const getTodayMovements = async (outletId) => {
  const response = await api.get(`/dashboard/movements?outlet_id=${outletId}`);
  return response.data;
};

export const getInventoryByCategory = async (outletId) => {
  const response = await api.get(`/dashboard/inventory-by-category?outlet_id=${outletId}`);
  return response.data;
};

export const getTopMovers = async (outletId, days = 7) => {
  const response = await api.get(`/dashboard/top-movers?outlet_id=${outletId}&days=${days}`);
  return response.data;
};

export const getProcurementSummary = async (outletId) => {
  const response = await api.get(`/dashboard/procurement-summary?outlet_id=${outletId}`);
  return response.data;
};

// Ingredients
export const getIngredients = async (outletId) => {
  const response = await api.get(`/ingredients?outlet_id=${outletId}`);
  return response.data;
};

export const getIngredientLedger = async (ingredientId, outletId) => {
  const response = await api.get(`/ingredients/${ingredientId}/ledger?outlet_id=${outletId}`);
  return response.data;
};

// Purchase Sync
// (legacy POST /api/purchases/sync removed — see commit 3133abf 2026-04-28;
//  global re-sync goes through POST /api/sync/all?force=true on the Dashboard)

// Sales Upload
export const parseSalesFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/inventory/sales/upload/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getSalesTemplates = async () => {
  const response = await api.get('/inventory/sales/templates');
  return response.data;
};

export const saveSalesTemplate = async (data) => {
  const response = await api.post('/inventory/sales/templates', data);
  return response.data;
};

export const deleteSalesTemplate = async (templateId) => {
  const response = await api.delete(`/inventory/sales/templates/${templateId}`);
  return response.data;
};

export const getItemMappings = async () => {
  const response = await api.get('/inventory/sales/item-mappings');
  return response.data;
};

export const saveItemMapping = async (data) => {
  const response = await api.post('/inventory/sales/item-mappings', data);
  return response.data;
};

export const ingestSalesUpload = async (data) => {
  const response = await api.post('/inventory/sales/upload/ingest', data);
  return response.data;
};

export const getSalesUploadHistory = async (outletId) => {
  const response = await api.get(`/inventory/sales/upload/history${outletId ? `?outlet_id=${outletId}` : ''}`);
  return response.data;
};

export const getUnmappedItems = async () => {
  const response = await api.get('/inventory/sales/unmapped-items');
  return response.data;
};

// Sales Capture Engine
export const triggerSalesCaptureSync = async (data) => {
  const response = await api.post('/sales-capture/sync', data);
  return response.data;
};

export const getSalesCaptureSyncStatus = async (outletId) => {
  const response = await api.get(`/sales-capture/sync-status${outletId ? `?outlet_id=${outletId}` : ''}`);
  return response.data;
};

export const getSalesCaptureSyncRuns = async (outletId, limit = 10) => {
  const response = await api.get(`/sales-capture/sync-runs?limit=${limit}${outletId ? `&outlet_id=${outletId}` : ''}`);
  return response.data;
};

export const getSalesCaptureUnresolved = async (outletId, businessDate) => {
  let url = `/sales-capture/unresolved?`;
  if (outletId) url += `outlet_id=${outletId}&`;
  if (businessDate) url += `business_date=${businessDate}&`;
  const response = await api.get(url);
  return response.data;
};

export const getSalesCaptureConsumption = async (date, outletId) => {
  const response = await api.get(`/sales-capture/consumption/${date}${outletId ? `?outlet_id=${outletId}` : ''}`);
  return response.data;
};

export const getSalesCaptureUnmappedImpact = async (outletId, sortBy = 'quantity', limit = 20) => {
  let url = `/sales-capture/unmapped-impact?sort_by=${sortBy}&limit=${limit}`;
  if (outletId) url += `&outlet_id=${outletId}`;
  const response = await api.get(url);
  return response.data;
};

// Phase 2 — Mapping velocity (May 7 2026)
export const getMappingSuggestions = async (itemNames, maxCandidates = 3) => {
  const response = await api.post('/sales-capture/mapping-suggestions', {
    item_names: itemNames,
    max_candidates: maxCandidates,
  });
  return response.data;
};

export const publishAndMap = async ({ item_name, recipe_id, item_id, item_category, outlet_id }) => {
  const response = await api.post('/sales-capture/publish-and-map', {
    item_name, recipe_id, item_id, item_category, outlet_id,
  });
  return response.data;
};

export const bulkMappingImport = async ({ outlet_id, rows, auto_publish = false, dry_run = false }) => {
  const response = await api.post('/sales-capture/bulk-mapping-import', {
    outlet_id, rows, auto_publish, dry_run,
  });
  return response.data;
};

// Slice 2 — Depletion Readiness (May 7 2026)
export const getDepletionReadiness = async (outletId, softCredit) => {
  const params = new URLSearchParams();
  if (outletId) params.set('outlet_id', outletId);
  if (softCredit !== undefined && softCredit !== null) params.set('soft_credit', softCredit);
  const qs = params.toString();
  const response = await api.get(`/sales-capture/depletion-readiness${qs ? `?${qs}` : ''}`);
  return response.data;
};

// Slice 3 — Mode Promotion (May 7 2026)
export const getModeStatus = async (outletId) => {
  const params = new URLSearchParams();
  if (outletId) params.set('outlet_id', outletId);
  const qs = params.toString();
  const response = await api.get(`/sales-capture/mode-status${qs ? `?${qs}` : ''}`);
  return response.data;
};

export const promoteMode = async ({ outlet_id, target_mode, reason, confirm_consequences = true, coverage_threshold, readiness_threshold }) => {
  const response = await api.post('/sales-capture/promote-mode', {
    outlet_id, target_mode, reason, confirm_consequences,
    coverage_threshold, readiness_threshold,
  });
  return response.data;
};

export const getModeAudit = async (outletId, limit = 50) => {
  const params = new URLSearchParams();
  if (outletId) params.set('outlet_id', outletId);
  params.set('limit', String(limit));
  const response = await api.get(`/sales-capture/mode-audit?${params.toString()}`);
  return response.data;
};

// Preps
//
// Backwards-compatible: `getPreps()` (no args) keeps the legacy contract.
// `getPreps({ search })` adds the new usage-aware search wired to the
// /api/preps?search= backend (May 6 2026).
export const getPreps = async (params = {}) => {
  const response = await api.get('/preps', { params });
  return response.data;
};

export const producePrep = async (prepId, outletId, actualYield) => {
  const response = await api.post(`/preps/produce?prep_id=${prepId}&outlet_id=${outletId}&actual_yield=${actualYield}`);
  return response.data;
};

export const createPrep = async (data) => {
  const response = await api.post('/preps', data);
  return response.data;
};

// Transfers
export const getTransfers = async (outletId) => {
  const response = await api.get(`/transfers?outlet_id=${outletId}`);
  return response.data;
};

export const requestTransfer = async (data) => {
  const response = await api.post('/transfers/request', data);
  return response.data;
};

export const approveTransfer = async (transferId) => {
  const response = await api.post(`/transfers/${transferId}/approve`);
  return response.data;
};

// Audits
export const getAudits = async (outletId) => {
  const response = await api.get(`/audits?outlet_id=${outletId}`);
  return response.data;
};

export const startAudit = async (outletId, auditDate = null) => {
  const qs = new URLSearchParams({ outlet_id: outletId });
  if (auditDate) qs.set('audit_date', auditDate);
  const response = await api.post(`/audits/start?${qs.toString()}`);
  return response.data;
};

export const submitAudit = async (auditId, lines) => {
  const response = await api.post(`/audits/${auditId}/submit`, { lines });
  return response.data;
};

// Intelligence
export const getForecast = async (outletId) => {
  const response = await api.get(`/intelligence/forecast?outlet_id=${outletId}`);
  return response.data;
};

export const getTheftDetection = async (outletId) => {
  const response = await api.get(`/intelligence/theft-detection?outlet_id=${outletId}`);
  return response.data;
};

export const getAnomalies = async (outletId) => {
  const response = await api.get(`/intelligence/anomalies?outlet_id=${outletId}`);
  return response.data;
};

// Opening Stock
export const getOpeningStockStatus = async (outletId) => {
  const response = await api.get(`/inventory/opening-stock/status?outlet_id=${outletId}`);
  return response.data;
};

export const initializeOpeningStock = async (outletId, lines, options = {}) => {
  const payload = {
    outlet_id: outletId,
    lines,
  };
  if (options.opening_business_date) {
    payload.opening_business_date = options.opening_business_date;
  }
  if (options.opening_stock_confidence) {
    payload.opening_stock_confidence = options.opening_stock_confidence;
  }
  if (options.override) {
    payload.override = true;
  }
  if (options.request_id) {
    payload.request_id = options.request_id;
  }
  // Generous timeout: large CSV uploads can take >60s through some proxies.
  // The handler is fast but the response trip is what matters here.
  const response = await api.post('/inventory/opening-stock', payload, {
    timeout: 120000,
  });
  return response.data;
};

// Sales Consumption
export const consumeSales = async (data) => {
  const response = await api.post('/inventory/sales/consume', data);
  return response.data;
};

// ============================================================================
// RECIPES MODULE (Phase 3.0)
// ============================================================================

// List recipes with optional filters
export const getRecipes = async (params = {}) => {
  const response = await api.get('/inventory/recipes', { params });
  return response.data;
};

// Get single recipe by ID
export const getRecipe = async (recipeId) => {
  const response = await api.get(`/inventory/recipes/${recipeId}`);
  return response.data;
};

// Create a new recipe
export const createRecipe = async (data) => {
  const response = await api.post('/inventory/recipes', data);
  return response.data;
};

// Update an existing recipe
export const updateRecipe = async (recipeId, data) => {
  const response = await api.put(`/inventory/recipes/${recipeId}`, data);
  return response.data;
};

// Publish a recipe (DRAFT -> ACTIVE)
export const publishRecipe = async (recipeId) => {
  const response = await api.post(`/inventory/recipes/${recipeId}/publish`);
  return response.data;
};

// Archive a recipe (ACTIVE -> ARCHIVED)
export const archiveRecipe = async (recipeId) => {
  const response = await api.post(`/inventory/recipes/${recipeId}/archive`);
  return response.data;
};

// Delete a recipe (only DRAFT or ARCHIVED)
export const deleteRecipe = async (recipeId) => {
  const response = await api.delete(`/inventory/recipes/${recipeId}`);
  return response.data;
};

// Delete or archive a prep
export const deletePrep = async (prepId) => {
  const response = await api.delete(`/preps/${prepId}`);
  return response.data;
};

// Update an existing prep (RBAC-gated to org_admin / super_admin server-side)
export const updatePrep = async (prepId, payload) => {
  const response = await api.put(`/preps/${prepId}`, payload);
  return response.data;
};

// Org-wide ingredients master (full canonical list, with effective_price if outlet provided)
export const getIngredientsMaster = async (outletId, includePricing = true) => {
  const params = new URLSearchParams();
  if (outletId) params.set('outlet_id', outletId);
  params.set('include_pricing', includePricing ? 'true' : 'false');
  const response = await api.get(`/ingredients/master?${params.toString()}`);
  return response.data;
};

// Rollup recipe cost and store snapshot
export const rollupRecipeCost = async (recipeId) => {
  const response = await api.post(`/inventory/recipes/${recipeId}/cost/rollup`);
  return response.data;
};

// Get unmapped POS items for an outlet
export const getUnmappedPOSItems = async (outletId) => {
  const response = await api.get('/inventory/recipes/pos/unmapped', {
    params: { outlet_id: outletId }
  });
  return response.data;
};

// Map POS items to recipes
export const mapPOSToRecipes = async (outletId, mappings) => {
  const response = await api.post('/inventory/recipes/pos/map', {
    outlet_id: outletId,
    mappings
  });
  return response.data;
};

// Get POS menu items for an outlet
export const getPOSMenuItems = async (outletId) => {
  const response = await api.get('/inventory/pos/menu-items', {
    params: { outlet_id: outletId }
  });
  return response.data;
};

// POS sales webhook (for testing)
export const postPOSSalesEvent = async (data) => {
  const response = await api.post('/inventory/pos/events/sales', data);
  return response.data;
};

// POS pull sync
export const syncPOSSales = async (outletId, from, to) => {
  const response = await api.post('/inventory/pos/sync/sales', null, {
    params: { outlet_id: outletId, from, to }
  });
  return response.data;
};

// Trial Purchases
export const syncTrialPurchases = async (outletId, since = null) => {
  const response = await api.post('/trials/sync', null, {
    params: { outlet_id: outletId, since }
  });
  return response.data;
};

export const getTrialsSummary = async (outletId) => {
  const response = await api.get('/trials/summary', {
    params: { outlet_id: outletId }
  });
  return response.data;
};

// ============================================================================
// PROCUREMENT MODULE (Phase 1B - PO Generation)
// ============================================================================

// Generate purchase orders from procurement plan items
export const generatePurchaseOrders = async (outletId, items) => {
  const response = await api.post('/inventory/procurement/po/generate', {
    outlet_id: outletId,
    items
  });
  return response.data;
};

// List purchase orders for an outlet
export const getPurchaseOrders = async (outletId, status = null) => {
  const params = { outlet_id: outletId };
  if (status) params.status = status;
  const response = await api.get('/inventory/procurement/po', { params });
  return response.data;
};

// Get a specific purchase order
export const getPurchaseOrder = async (poId) => {
  const response = await api.get(`/inventory/procurement/po/${poId}`);
  return response.data;
};

// Approve a purchase order
export const approvePurchaseOrder = async (poId, notes = null) => {
  const response = await api.post(`/inventory/procurement/po/${poId}/approve`, { notes });
  return response.data;
};

// Cancel a purchase order
export const cancelPurchaseOrder = async (poId) => {
  const response = await api.post(`/inventory/procurement/po/${poId}/cancel`);
  return response.data;
};

// ============================================================================
// DEMAND OVERRIDES
// ============================================================================

// Get demand overrides for an outlet
export const getDemandOverrides = async (outletId) => {
  const response = await api.get('/inventory/procurement/demand-overrides', {
    params: { outlet_id: outletId }
  });
  return response.data;
};

// Create a demand override
export const createDemandOverride = async (data) => {
  const response = await api.post('/inventory/procurement/demand-overrides', data);
  return response.data;
};

// Delete a demand override
export const deleteDemandOverride = async (overrideId) => {
  const response = await api.delete(`/inventory/procurement/demand-overrides/${overrideId}`);
  return response.data;
};

// ============================================================================
// FORECAST ACCURACY (Phase 2)
// ============================================================================

// Get forecast accuracy summary for an outlet
export const getForecastAccuracy = async (outletId, periodDays = 7) => {
  const response = await api.get('/inventory/procurement/forecast/accuracy', {
    params: { outlet_id: outletId, period_days: periodDays }
  });
  return response.data;
};

// Get forecast history for a specific ingredient
export const getIngredientForecastHistory = async (outletId, ingredientId, days = 14) => {
  const response = await api.get(`/inventory/procurement/forecast/history/${ingredientId}`, {
    params: { outlet_id: outletId, days }
  });
  return response.data;
};

// Update forecast actuals (compare forecast vs actual)
export const updateForecastActuals = async (outletId, forecastDate) => {
  const response = await api.post('/inventory/procurement/forecast/update-actuals', null, {
    params: { outlet_id: outletId, forecast_date: forecastDate }
  });
  return response.data;
};

// ============================================================================
// LATE PRE-AUDIT PURCHASE RECONCILIATION
// ============================================================================

export const getPurchaseReconciliationQueue = async (outletId, status = 'PENDING') => {
  const response = await api.get('/inventory/purchase-reconciliation/queue', {
    params: { outlet_id: outletId, status },
  });
  return response.data;
};

export const resolvePurchaseReconciliation = async (queueId, decision, uncountedQuantity = null, notes = null) => {
  const body = { decision };
  if (uncountedQuantity !== null && uncountedQuantity !== undefined) {
    body.uncounted_quantity = uncountedQuantity;
  }
  if (notes) body.notes = notes;
  const response = await api.post(`/inventory/purchase-reconciliation/${queueId}/resolve`, body);
  return response.data;
};

export default api;
